<template>
  <div ref="root">
    <!-- Filtro de período dos indicadores. O título "Mural" já vem do layout
         (meta.pageTitle da rota) — repeti-lo aqui duplicava o cabeçalho. -->
    <div class="flex flex-wrap items-center justify-end mural-head">
      <div class="flex flex-wrap items-center" style="gap: .6rem;">
        <span class="mural-head__range">{{ rangeLabel }}</span>
        <b-button-group size="sm">
          <b-button
            v-for="option in periodOptions"
            :key="option.value"
            :variant="period === option.value ? 'primary' : 'outline-primary'"
            @click="setPeriod(option.value)">
            {{ option.label }}
          </b-button>
        </b-button-group>
      </div>
    </div>

    <div v-if="period === 'custom'" class="flex flex-wrap items-center justify-end mb-1" style="gap: .6rem;">
      <b-form-input type="date" v-model="customFrom" size="sm" class="mural-date" />
      <small class="text-muted">{{ $t('mural.period.to') }}</small>
      <b-form-input type="date" v-model="customTo" size="sm" class="mural-date" />
      <b-button variant="primary" size="sm" @click="loadSummary">{{ $t('mural.period.apply') }}</b-button>
    </div>

    <!-- Avisos de cadastro/recebimento (comportamento anterior do Mural) -->
    <b-alert show variant="warning" class="p-1 mb-1" v-if="showStripeWarning">
      <feather-icon icon="AlertTriangleIcon" class="mr-50" />
      Sua conta de recebimento (Stripe) ainda não está conectada — enquanto isso, as vendas pelo
      Stripe ficam integralmente na conta da plataforma.
      <router-link to="/my-account" class="font-weight-bold">Conectar agora</router-link>
    </b-alert>

    <!-- Recusado: motivo do analista + reenvio direto daqui quando não há mais pendência. -->
    <b-alert show variant="danger" class="p-1 mb-1" v-if="kycStatus === 'RJ'">
      <feather-icon icon="XCircleIcon" class="mr-50" />
      Seu cadastro foi recusado pela equipe Hubee<span v-if="kycNotes">: <em>{{ kycNotes }}</em></span>.
      <template v-if="checklistPendente.length">
        Falta {{ checklistPendente.join(', ') }}.
        <router-link :to="completeRegistrationPath" class="font-weight-bold">Corrigir cadastro</router-link>
      </template>
      <template v-else>
        <router-link :to="completeRegistrationPath" class="font-weight-bold">Revisar cadastro</router-link>
        <b-button size="sm" variant="danger" class="ml-1" :disabled="resubmitting" @click="resubmitKyc">
          Reenviar para análise
        </b-button>
      </template>
    </b-alert>

    <b-alert show variant="warning" class="p-1 mb-1" v-else-if="checklistPendente.length">
      <feather-icon icon="AlertTriangleIcon" class="mr-50" />
      Seu cadastro ainda não está completo — falta {{ checklistPendente.join(', ') }}.
      <router-link :to="completeRegistrationPath" class="font-weight-bold">Completar cadastro</router-link>
    </b-alert>

    <!-- Só para quem ainda não está ativo: kyc PE é o default histórico, e uma
         entidade já ativa com PE não está "em análise" nenhuma. -->
    <b-alert show variant="info" class="p-1 mb-1" v-else-if="kycStatus === 'PE' && entityStatus !== 'AC'">
      <feather-icon icon="ClockIcon" class="mr-50" />
      Seu cadastro está completo e em análise pela equipe Hubee — você recebe a resposta por e-mail.
    </b-alert>

    <div v-permission="'dashboard'">
      <mural-kpi-row
        v-if="summary"
        :summary="summary"
        :money-hidden="moneyHidden"
        :period="period"
        :scroll="isNarrow"
        @toggle-values="toggleMoneyHidden" />

      <div class="mural-layout" :class="{ 'mural-layout--stacked': isNarrow }">
        <mural-agenda
          :days="agenda.days"
          :classes="agenda.classes"
          :view="view"
          :anchor="anchor"
          @update:view="onViewChange"
          @update:anchor="onAnchorChange"
          @select="openOccurrence" />

        <div class="flex flex-column" style="gap: 1.25rem; min-width: 0;">
          <mural-upcoming
            :days="agenda.days"
            :classes="agenda.classes"
            @select="openOccurrence"
            @go-agenda="goToday" />

          <!-- Só aparece se o backend enviou vendas: é o mesmo sinal que
               esconde a receita do professor. -->
          <mural-sales
            v-if="summary && summary.sales"
            :sales="summary.sales"
            :total="summary.sales_total"
            :period-label="periodChip" />
        </div>
      </div>
    </div>

    <mural-occurrence-drawer
      v-if="selected"
      :date="selected.date"
      :slot="selected.slot"
      :lesson="selected.lesson"
      @close="selected = null"
      @action="onAction" />

    <mural-action-modal ref="actionModal" @saved="onActionSaved" />
  </div>
</template>

<script>
import { BAlert, BButton, BButtonGroup, BFormInput } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import AgendaService from '@/services/api/AgendaService'
import LegalEntityService from '@/services/api/LegalEntityService'
import LessonService from '@/services/api/LessonService'
import StripeConnectService from '@/services/api/StripeConnectService'
import MuralActionModal from './mural/MuralActionModal.vue'
import MuralAgenda from './mural/MuralAgenda.vue'
import MuralKpiRow from './mural/MuralKpiRow.vue'
import MuralOccurrenceDrawer from './mural/MuralOccurrenceDrawer.vue'
import MuralSales from './mural/MuralSales.vue'
import MuralUpcoming from './mural/MuralUpcoming.vue'
import { MONTH_NAMES, mondayOf, toDateString } from './mural/muralAgenda'
import { userRoleNames } from '@/util/Util'

/**
 * Mural: a tela de acompanhamento do estabelecimento (ou do professor).
 *
 * Dois controles independentes governam a tela, e é de propósito: o filtro de
 * período no topo manda nos indicadores e nas vendas ("como foi a semana"),
 * enquanto a navegação da agenda manda na grade ("o que acontece quinta"). Um
 * só controle obrigaria a mudar o recorte do negócio para olhar outro dia.
 *
 * Nada aqui decide o que o usuário pode ver: o escopo (clube ou professor) e a
 * presença dos valores monetários vêm resolvidos do backend.
 */
export default {
  components: {
    BAlert,
    BButton,
    BButtonGroup,
    BFormInput,
    MuralActionModal,
    MuralAgenda,
    MuralKpiRow,
    MuralOccurrenceDrawer,
    MuralSales,
    MuralUpcoming,
  },
  data() {
    return {
      agendaService: null,
      lessonService: null,
      stripeConnectService: null,
      legalEntityService: null,
      contentWidth: 0,
      period: 'week',
      customFrom: '',
      customTo: '',
      summary: null,
      // null = ainda não escolhido pelo usuário; `view` abaixo decide pela
      // largura. Fixar 'week' aqui obrigaria a corrigir depois da medição, e a
      // tela piscaria trocando de visão sozinha.
      viewOverride: null,
      anchor: toDateString(new Date()),
      agenda: { classes: [], days: [] },
      selected: null,
      showStripeWarning: false,
      checklistPendente: [],
      // Situação de habilitação (KYC) da própria entidade — decide qual banner
      // aparece e habilita o reenvio pós-recusa direto do Mural.
      kycStatus: null,
      kycNotes: null,
      kycPersonType: null,
      kycEntityId: null,
      entityStatus: null,
      resubmitting: false,
    }
  },
  computed: {
    ...mapGetters('moneyVisibility', ['moneyHidden']),
    /**
     * Mede a própria largura, e não a da janela: o menu lateral come 260px e
     * pode ser recolhido, então a mesma janela de 1280px comporta ou não as duas
     * colunas dependendo do estado do menu.
     */
    isNarrow() {
      return this.contentWidth > 0 && this.contentWidth < 1080
    },
    /**
     * Para onde o link de "completar/corrigir cadastro" aponta: o atalho
     * /my-account redireciona para a ficha de PROFISSIONAL, que é NE-shaped —
     * o responsável de estabelecimento edita pela ficha do estabelecimento.
     */
    completeRegistrationPath() {
      if (this.kycPersonType === 'LE' && this.kycEntityId) {
        return `/legal_entities/${this.kycEntityId}/edit`
      }
      return '/my-account'
    },
    /** Escolha do usuário, ou o padrão que a largura pede. */
    view() {
      if (this.viewOverride) return this.viewOverride
      return this.isNarrow ? 'day' : 'week'
    },
    periodOptions() {
      return [
        { value: 'week', label: this.$t('mural.period.week') },
        { value: 'month', label: this.$t('mural.period.month') },
        { value: 'custom', label: this.$t('mural.period.custom') },
      ]
    },
    /** Janela dos indicadores — independente da janela da agenda. */
    periodRange() {
      const today = new Date()

      if (this.period === 'month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1)
        const last = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        return { start: toDateString(first), end: toDateString(last) }
      }

      if (this.period === 'custom') {
        return { start: this.customFrom, end: this.customTo }
      }

      const monday = mondayOf(today)
      const sunday = new Date(monday)
      sunday.setDate(sunday.getDate() + 6)
      return { start: toDateString(monday), end: toDateString(sunday) }
    },
    rangeLabel() {
      const { start, end } = this.periodRange
      if (!start || !end) return ''

      if (this.period === 'month') {
        const date = new Date()
        const name = MONTH_NAMES[date.getMonth()]
        return `${name.charAt(0).toUpperCase()}${name.slice(1)} de ${date.getFullYear()}`
      }

      return `${this.shortDate(start)} – ${this.shortDate(end)}`
    },
    periodChip() {
      if (this.period === 'month') {
        const name = MONTH_NAMES[new Date().getMonth()]
        return `${name.charAt(0).toUpperCase()}${name.slice(1)}`
      }
      return this.period === 'week' ? 'Esta semana' : 'Período'
    },
    /**
     * Janela da agenda. O dia carrega a semana inteira porque a faixa de dias
     * acima da lista precisa dos sete; o mês carrega as 6 semanas da grade.
     */
    agendaWindow() {
      const anchorDate = new Date(`${this.anchor}T00:00:00`)

      if (this.view === 'month') {
        const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1)
        return { start: toDateString(mondayOf(first)), days: 42 }
      }

      return { start: toDateString(mondayOf(anchorDate)), days: 7 }
    },
  },
  methods: {
    shortDate(value) {
      const [year, month, day] = String(value).split('-')
      return `${day}/${month}/${year}`
    },
    /**
     * A preferência é do usuário, não da tela: o módulo grava em localStorage e
     * marca o `<html>`, então o Mural e o painel do influencer concordam.
     */
    toggleMoneyHidden() {
      this.$store.dispatch('moneyVisibility/toggleMoneyHidden')
    },
    setPeriod(value) {
      this.period = value

      if (value === 'custom') {
        // Pré-preenche com a semana corrente para o usuário só ajustar as bordas.
        if (!this.customFrom || !this.customTo) {
          const monday = mondayOf(new Date())
          this.customFrom = toDateString(monday)
          this.customTo = toDateString(new Date())
        }
        return
      }

      this.loadSummary()
    },
    loadSummary() {
      const { start, end } = this.periodRange
      if (!start || !end) return

      this.agendaService.getSummary(start, end).then(
        data => { this.summary = data },
        () => {},
      )
    },
    loadAgenda() {
      const { start, days } = this.agendaWindow

      this.agendaService.getOccurrences(start, days).then(
        data => {
          this.agenda = { classes: data.classes || [], days: data.days || [] }
          this.refreshSelected()
        },
        () => { this.agenda = { classes: [], days: [] } },
      )
    },
    /**
     * Depois de uma ação, o drawer precisa refletir o novo estado da ocorrência
     * — fechá-lo obrigaria o usuário a reabrir para conferir o que mudou.
     */
    refreshSelected() {
      if (!this.selected) return

      const day = this.agenda.days.find(item => item.date === this.selected.date)
      const slot = day && (day.slots || []).find(
        item => item.lessonId === this.selected.slot.lessonId
          && item.startTime === this.selected.slot.startTime,
      )

      if (!slot) {
        this.selected = null
        return
      }

      const lesson = this.agenda.classes.find(item => item.id === slot.lessonId) || this.selected.lesson
      this.selected = { date: this.selected.date, slot, lesson }
    },
    onViewChange(view) {
      const previous = this.view
      this.viewOverride = view
      // Só o mês muda o tamanho da janela; dia e semana compartilham os 7 dias.
      if (previous === 'month' || view === 'month') this.loadAgenda()
    },
    onAnchorChange(anchor) {
      const previousWindow = this.agendaWindow
      this.anchor = anchor
      const nextWindow = this.agendaWindow

      if (previousWindow.start !== nextWindow.start || previousWindow.days !== nextWindow.days) {
        this.loadAgenda()
      }
    },
    goToday() {
      this.viewOverride = null
      this.onAnchorChange(toDateString(new Date()))
    },
    openOccurrence(payload) {
      this.selected = payload
    },
    onAction(mode) {
      if (mode === 'unblock') {
        this.removeOverride()
        return
      }

      this.$refs.actionModal.open(mode, this.selected)
    },
    /**
     * Reabrir a data é remover a exceção, não criar outra: sem isso a ocorrência
     * ficaria com um override "ativo" vazio no lugar do bloqueio.
     */
    removeOverride() {
      const { lesson, slot } = this.selected
      if (!slot.override_id) return

      this.$vs.loading()
      this.lessonService.deleteOccurrenceOverride(lesson.id, slot.override_id).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('mural.toast.unblocked'))
          this.loadAgenda()
        },
        error => this.notifyErrors(this.$vs, error),
      ).finally(() => { this.$vs.loading.close() })
    },
    onActionSaved() {
      this.loadAgenda()
      this.loadSummary()
    },
    loadOnboardingWarnings() {
      // Só quem está preso a uma única entidade (o caso do profissional autônomo
      // E do admin do estabelecimento) recebe o aviso de recebimento — quem
      // administra várias já vê a situação entidade a entidade na própria tela
      // de gestão.
      if (!this.$store.getters['dataAccess/globalInstitutionLocked']) return

      const legalEntityId = this.$store.getters['dataAccess/activeInstitutionId']
      if (!legalEntityId) return

      this.stripeConnectService = StripeConnectService.build(this.$vs)
      this.stripeConnectService.getStatus(legalEntityId).then(
        status => { this.showStripeWarning = !status?.connected },
        () => {
          // Sem gateway configurado ou erro de rede: melhor não mostrar aviso
          // errado do que travar o Mural por causa dele.
        },
      )

      // A checklist de habilitação vale para os DOIS tipos de fornecedor desde
      // o auto-cadastro de parceiros (rotas /kyc/* decidem os itens pelo
      // person_type). O recorte continua pelo papel: `club_admin` (grupo legado
      // de clube) fica de fora como sempre ficou; quem entra pelo wizard nasce
      // com `professional` (PF) ou `establishment` (PJ).
      const papeis = userRoleNames()
      if (!papeis.includes('professional') && !papeis.includes('establishment')) return

      this.kycEntityId = legalEntityId
      this.legalEntityService = LegalEntityService.build(this.$vs)

      // A entidade traz kyc_status/kyc_notes/person_type — é o que decide qual
      // banner aparece e para onde aponta o link de correção.
      this.legalEntityService.get(`/${legalEntityId}`).then(
        entity => {
          if (!entity) return
          this.kycStatus = entity.kyc_status || null
          this.kycNotes = entity.kyc_notes || null
          this.kycPersonType = entity.person_type || null
          this.entityStatus = entity.status || null
        },
        () => {},
      )

      this.legalEntityService.kycChecklist(legalEntityId).then(
        checklist => {
          this.checklistPendente = (Array.isArray(checklist) ? checklist : [])
            .filter(item => !item.ok)
            .map(item => item.label)
        },
        () => {},
      )
    },
    /** Reenvio pós-recusa, direto do Mural — o mesmo gesto para NE e LE. */
    resubmitKyc() {
      if (!this.kycEntityId || this.resubmitting) return

      this.resubmitting = true
      this.legalEntityService.kycSubmitForReview(this.kycEntityId).then(
        () => {
          this.notifySuccess(this.$vs, 'Cadastro reenviado para análise. Você recebe a resposta por e-mail.')
          this.loadOnboardingWarnings()
        },
        error => { this.showErrors(null, error) },
      ).finally(() => { this.resubmitting = false })
    },
    reload() {
      this.selected = null
      this.loadAgenda()
      this.loadSummary()
    },
    measure() {
      const element = this.$refs.root
      if (!element) return

      this.contentWidth = element.getBoundingClientRect().width

      // ResizeObserver pega o que o `resize` da janela não pega — recolher o menu
      // lateral muda a largura útil sem mudar a da janela. Os dois juntos porque
      // o observer só dispara com a página compondo quadros, e o listener cobre
      // o caso da aba em segundo plano.
      if (!this.resizeObserver && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(entries => {
          this.contentWidth = entries[0].contentRect.width
        })
        this.resizeObserver.observe(element)
      }
    },
    onWindowResize() {
      this.measure()
    },
  },
  beforeMount() {
    this.agendaService = AgendaService.build(this.$vs)
    this.lessonService = LessonService.build(this.$vs)
  },
  async mounted() {
    if (!this.$store.getters['dataAccess/loaded']) {
      await this.$store.dispatch('dataAccess/setupFromLogin')
    }

    // No `mounted` o menu lateral ainda está assentando e a área útil mede menos
    // do que vai medir — sem remedir depois do primeiro quadro, um desktop abre
    // no layout de telefone. O `setTimeout` cobre a transição do menu, que é mais
    // longa que um quadro.
    this.measure()
    requestAnimationFrame(this.measure)
    this.measureTimer = setTimeout(this.measure, 400)

    this.loadAgenda()
    this.loadSummary()
    this.loadOnboardingWarnings()

    // A agenda é por estabelecimento; trocar o clube no navbar tem de recarregar.
    this.$root.$on('activeLegalEntityChanged', this.reload)
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    this.$root.$off('activeLegalEntityChanged', this.reload)
    window.removeEventListener('resize', this.onWindowResize)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.measureTimer) clearTimeout(this.measureTimer)
  },
}
</script>

<style lang="scss" scoped>
.mural-head {
  gap: 0.75rem 1rem;
  margin-bottom: 1.1rem;

  &__range {
    font-size: 0.857rem;
    color: var(--text-body);
    white-space: nowrap;
  }
}

.mural-date {
  width: auto;
}

.mural-layout {
  display: grid;
  grid-template-columns: minmax(0, 2.35fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  &--stacked {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
